import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Plus, X } from 'lucide-react';

const ActivitiesForm = ({ data, onSave }) => {
  const [activities, setActivities] = useState(data && data.length > 0 ? data : []);

  const activityTypes = [
    'Academic',
    'Art',
    'Athletics',
    'Career Oriented',
    'Community Service',
    'Computer/Technology',
    'Cultural',
    'Dance',
    'Debate/Speech',
    'Environmental',
    'Family Responsibilities',
    'Journalism/Publication',
    'Music',
    'Religious',
    'Research',
    'Robotics',
    'School Spirit',
    'Science/Math',
    'Social Justice',
    'Student Government',
    'Theater/Drama',
    'Work',
    'Other'
  ];

  const gradeOptions = ['9', '10', '11', '12', 'Post-Graduate'];

  const addActivity = () => {
    if (activities.length < 10) {
      setActivities([...activities, {
        name: '',
        type: '',
        position: '',
        description: '',
        years_participated: [],
        hours_per_week: '',
        weeks_per_year: ''
      }]);
    }
  };

  const removeActivity = (index) => {
    setActivities(activities.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const updated = [...activities];
    updated[index][field] = value;
    setActivities(updated);
  };

  const handleGradeToggle = (index, grade) => {
    const updated = [...activities];
    const years = updated[index].years_participated || [];
    if (years.includes(grade)) {
      updated[index].years_participated = years.filter(y => y !== grade);
    } else {
      updated[index].years_participated = [...years, grade];
    }
    setActivities(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(activities);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-900">
          <strong>Tip:</strong> You can add up to 10 activities. Focus on quality over quantity - describe your most meaningful experiences.
        </p>
      </div>

      {activities.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-gray-600 mb-4">No activities added yet</p>
          <Button type="button" onClick={addActivity}>
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Activity
          </Button>
        </Card>
      )}

      {activities.map((activity, index) => (
        <Card key={index} className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Activity {index + 1}</h3>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeActivity(index)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor={`name-${index}`}>Activity Name *</Label>
                <Input
                  id={`name-${index}`}
                  value={activity.name}
                  onChange={(e) => handleChange(index, 'name', e.target.value)}
                  placeholder="e.g., Debate Team"
                  required
                />
              </div>
              <div>
                <Label htmlFor={`type-${index}`}>Activity Type *</Label>
                <Select
                  value={activity.type}
                  onValueChange={(value) => handleChange(index, 'type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {activityTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor={`position-${index}`}>Position/Leadership *</Label>
              <Input
                id={`position-${index}`}
                value={activity.position}
                onChange={(e) => handleChange(index, 'position', e.target.value)}
                placeholder="e.g., Team Captain, Member, President"
                required
              />
            </div>

            <div>
              <Label htmlFor={`description-${index}`}>Description *</Label>
              <Textarea
                id={`description-${index}`}
                value={activity.description}
                onChange={(e) => handleChange(index, 'description', e.target.value)}
                placeholder="Describe your role, achievements, and impact (max 150 words)"
                rows={4}
                maxLength={1000}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {activity.description?.length || 0}/1000 characters
              </p>
            </div>

            <div>
              <Label>Grade Levels Participated *</Label>
              <div className="flex flex-wrap gap-4 mt-2">
                {gradeOptions.map(grade => (
                  <div key={grade} className="flex items-center space-x-2">
                    <Checkbox
                      id={`grade-${index}-${grade}`}
                      checked={activity.years_participated?.includes(grade)}
                      onCheckedChange={() => handleGradeToggle(index, grade)}
                    />
                    <Label
                      htmlFor={`grade-${index}-${grade}`}
                      className="cursor-pointer font-normal"
                    >
                      Grade {grade}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor={`hours-${index}`}>Hours per Week *</Label>
                <Input
                  id={`hours-${index}`}
                  type="number"
                  min="0"
                  max="168"
                  value={activity.hours_per_week}
                  onChange={(e) => handleChange(index, 'hours_per_week', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor={`weeks-${index}`}>Weeks per Year *</Label>
                <Input
                  id={`weeks-${index}`}
                  type="number"
                  min="0"
                  max="52"
                  value={activity.weeks_per_year}
                  onChange={(e) => handleChange(index, 'weeks_per_year', e.target.value)}
                  required
                />
              </div>
            </div>
          </div>
        </Card>
      ))}

      {activities.length > 0 && activities.length < 10 && (
        <Button type="button" variant="outline" onClick={addActivity} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Add Another Activity ({activities.length}/10)
        </Button>
      )}

      <div className="flex justify-end">
        <Button type="submit">Save & Continue</Button>
      </div>
    </form>
  );
};

export default ActivitiesForm;